/**
 * Detect country from IP. Uses CORS-enabled services only (ipapi.co blocks browser CORS).
 * Returns country code (e.g. 'US', 'IL') or null on failure.
 */
export const detectCountryFromIP = async () => {
  // ipwho.is resolves by IP REGISTRATION (a Jordan-registered VPN IP → JO);
  // ipinfo.io resolves the datacenter's PHYSICAL location (virtual VPN → FR).
  // Prefer registration; fall back to ipinfo.io.
  const services = [
    { url: 'https://ipwho.is/', getCode: (d) => d?.country_code },
    { url: 'https://ipinfo.io/json', getCode: (d) => d?.country },
  ];
  for (const svc of services) {
    try {
      const res = await fetch(svc.url, { signal: AbortSignal.timeout(5000) });
      if (res.ok) {
        const data = await res.json();
        const code = svc.getCode(data);
        if (code && typeof code === 'string') return code.toUpperCase().slice(0, 2);
      }
    } catch (_) {
      /* try next */
    }
  }
  return null;
};

export const IP_CACHE_KEY = 'stylon_ip_country';
const IP_CACHE_TTL_MS = 60 * 60 * 1000; // 1h

const readFresh = () => {
  try {
    const raw = sessionStorage.getItem(IP_CACHE_KEY);
    if (!raw) return null;
    const { code, ts } = JSON.parse(raw);
    if (code && Date.now() - ts < IP_CACHE_TTL_MS) return code;
  } catch (_) { /* ignore */ }
  return null;
};

// Synchronous read of a fresh cached country code (or null). For first render.
export const readCachedCountrySync = () => readFresh();

// Async: returns cached code if fresh, else detects via IP and caches it.
export const getCachedCountryFromIP = async () => {
  const cached = readFresh();
  if (cached) return cached;
  const code = await detectCountryFromIP();
  try {
    if (code) sessionStorage.setItem(IP_CACHE_KEY, JSON.stringify({ code, ts: Date.now() }));
  } catch (_) { /* ignore */ }
  return code;
};
